import React, { useMemo } from 'react';

import { buildUnifiedAnalyticsTheme, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

interface UnifiedAnalyticsThemeProviderProps {
  children: React.ReactNode;
}

// Wraps buildUnifiedAnalyticsTheme, which carries the 2026 brand palette (Ocean Teal primary,
// Sandy Beige/Compass White light surfaces, Ocean Teal/#14545E dark surfaces) and Suisse type
// over whichever admin theme mode is active, then layers the shared analytics component shapes
// (8px buttons, cards, dialogs and inputs) and the keyboard focus ring on top, so every unified-shell
// and Lens surface renders with one palette, one type system and one shape language in both light
// and dark.
export function UnifiedAnalyticsThemeProvider({ children }: UnifiedAnalyticsThemeProviderProps) {
  const baseTheme = useTheme();
  const analyticsTheme = useMemo(() => withKeyboardFocusRing(buildUnifiedAnalyticsTheme(baseTheme)), [baseTheme]);

  return (
    <ThemeProvider theme={analyticsTheme}>
      {/*
        MUI's typography variants carry Suisse Int'l (see buildUnifiedAnalyticsTheme), but a
        plain-text node or a native form control renders whatever font-family is actually
        declared as CSS on an ancestor, not whatever the theme object says. display: contents
        keeps this element out of layout (it never becomes an extra flex/grid child) while still
        letting its own font-family cascade to every descendant, including native button, input,
        select and textarea elements, which the browser's UA stylesheet does not otherwise
        inherit from body. Scoped to this subtree only, never a global body rule that would leak
        into the wider admin.
      */}
      <Box
        sx={{
          display: 'contents',
          fontFamily: FONT_STACK_SUISSE_INTL,
          // `:where()` keeps the native-control reset deliberately low-specificity. A control
          // used as display copy (for example a clickable Suisse Works title) can then override
          // the inherited body face through its component-level `sx` without an `&&` workaround.
          '& :where(button, input, select, textarea)': { fontFamily: 'inherit' },
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
}

/**
 * One keyboard focus ring for every tab stop in the workspace.
 *
 * MUI's default focus treatment for an icon button is a ripple, which leaves
 * nothing behind once the ripple settles: a reader tabbing through the rail,
 * the composer or a card's action tray could not tell where they were. Each
 * component that noticed drew its own ring, so the shell carried several
 * shapes and offsets and the ones nobody noticed carried none.
 *
 * Declaring it once here means a control does not have to remember. The ring
 * is the accent colour, which is the mint that reads against the dark rail and
 * the teal that reads on a light card, so it stays visible on every surface
 * the shell paints. It is drawn on `:focus-visible` only, so a mouse click
 * never leaves a ring behind.
 */
function withKeyboardFocusRing(theme: Theme): Theme {
  const tokens = lensTokens(theme);
  const ring = {
    outline: `2px solid ${tokens.accent}`,
    outlineOffset: '2px',
  };
  return createTheme(theme, {
    components: {
      MuiButtonBase: { styleOverrides: { root: { '&:focus-visible': ring } } },
      MuiIconButton: { styleOverrides: { root: { '&:focus-visible': ring } } },
      MuiMenuItem: { styleOverrides: { root: { '&:focus-visible': ring } } },
      MuiChip: { styleOverrides: { root: { '&:focus-visible': ring } } },
      MuiLink: { styleOverrides: { root: { '&:focus-visible': ring } } },
    },
  });
}

export default UnifiedAnalyticsThemeProvider;
